"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, FileText, ShieldCheck, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const keyPoints = [{
  id: "1",
  label: "IP65/IP67 rated rugged handheld design"
}, {
  id: "2",
  label: "Fast non-destructive material identification"
}, {
  id: "3",
  label: "21 CFR Part 11 compliant software suite"
}];
export default function Hero() {
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Left Column: Information & CTAs */}
          <div className="flex flex-col items-start lg:col-span-7">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="gap-1.5 px-3 py-1 font-medium">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>Handheld NIR Spectroscopy</span>
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-muted-foreground">
                MicroNIR OnSite-W
              </Badge>
            </div>

            <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl">
              MicroNIR OnSite-W Handheld NIR Spectrometer
            </h1>

            <p className="mt-5 max-w-2xl font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
              A compact, wireless near-infrared spectrometer designed for rapid raw material identification, blend monitoring, and field analysis across regulated pharmaceutical and chemical manufacturing.
            </p>

            {/* Key feature bullets */}
            <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-4">
              {keyPoints.map((point, index) => <div key={point.id} data-index={index} className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{point.label}</span>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="h-11 px-6">
                <Link to="/BookADemoPage" className="inline-flex items-center gap-2">
                  <span>Book a Demonstration</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-11 px-6">
                <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" />
                  <span>Discuss Your Application</span>
                </Link>
              </Button>
            </div>

            {/* Supporting meta points */}
            <div className="mt-10 grid grid-cols-2 gap-4 border-t border-border pt-6 sm:grid-cols-3 sm:gap-6 w-full">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Spectral Range
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  950 – 1650 nm
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Connectivity
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  Bluetooth &amp; USB
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Compliance
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  USP &lt;1119&gt; / EP 2.2.40
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Visual Showcase */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
              <div className="overflow-hidden rounded-xl border border-border bg-card p-2 shadow-md">
                <AspectRatio ratio={4 / 3} className="overflow-hidden rounded-lg bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_onsite_w_spectrometer.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_onsite_w_spectrometer.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_onsite_w_spectrometer.png-wvc-sizes" alt="MicroNIR OnSite-W handheld analytical spectrometer" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </AspectRatio>
              </div>

              {/* Floating technical callout card */}
              <div className="mt-4 rounded-lg border border-border bg-card p-4 shadow-sm sm:absolute sm:-bottom-6 sm:-left-6 sm:mt-0 sm:max-w-xs">
                <div className="flex items-start gap-3">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Zap className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      Analytical Performance
                    </p>
                    <p className="text-sm font-semibold text-card-foreground">
                      Performance depends on the instrument, application, sample matrix and validated method
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}